
import { uploadDeliveryFile } from './files';
import { synczbase } from '../../lib/synczbase';
import { 
    NotificationSettings, FinancialSettings, InvoiceSettings, 
    BrandingSettings, MarketplaceSettings, IntegrationSecrets,
    NotificationTemplate, ModuleSettings, LegalPageSettings,
    TerminologySettings, PlatformSettings, DashboardSectionConfig
} from '../../types';

const DEFAULT_DASHBOARD_SECTIONS: DashboardSectionConfig[] = [
    { id: 'welcome_header', label: 'Welcome Header', enabled: true, order: 0, description: 'User greeting and date' },
    { id: 'thank_you', label: 'Thank You Banner', enabled: true, order: 1, description: 'Shows for Clients with completed orders' },
    { id: 'portfolio_links', label: 'Access Links', enabled: true, order: 2, description: 'Admin Only: Portal URLs' },
    { id: 'stats_cards', label: 'Statistics Grid', enabled: true, order: 3, description: 'Active, Pending, Revenue/Total cards' },
    { id: 'upsell_grid', label: 'Featured Services (Upsell)', enabled: true, order: 4, description: 'Random selection of services' },
    { id: 'quick_actions', label: 'Quick Actions', enabled: true, order: 5, description: 'Start Project, View Files, Support' }
];

function parseSettingData<T>(row: any): T | null {
    if (!row) return null;
    let dataPart: any = {};
    if (typeof row.data === 'string') {
        try { dataPart = JSON.parse(row.data); } catch (e) {}
    } else if (row.data && typeof row.data === 'object') {
        dataPart = row.data;
    } else if (typeof row.config === 'string') {
        try { dataPart = JSON.parse(row.config); } catch (e) {}
    } else if (row.config && typeof row.config === 'object') {
        dataPart = row.config;
    }
    
    // Also include any direct database columns (excluding metadata keys)
    const directCols: any = {};
    if (typeof row === 'object') {
        for (const k of Object.keys(row)) {
            if (!['id', 'config', 'data'].includes(k) && row[k] !== null && row[k] !== undefined) {
                directCols[k] = row[k];
            }
        }
    }

    const merged = { ...directCols, ...dataPart };
    return Object.keys(merged).length > 0 ? (merged as T) : null;
}

export const getPlatformSettings = async (): Promise<PlatformSettings> => {
    try {
        const res = await synczbase.query("SELECT * FROM settings WHERE id = $1 LIMIT 1", ['platform']);
        if (res && res.length > 0) {
            const data = parseSettingData<PlatformSettings>(res[0]) || {};
            if (!data.dashboardSections) {
                data.dashboardSections = DEFAULT_DASHBOARD_SECTIONS;
            }
            return data;
        }
    } catch(e) {}
    return { dashboardSections: DEFAULT_DASHBOARD_SECTIONS };
};

export const updatePlatformSettings = async (settings: PlatformSettings) => {
    const current = await getPlatformSettings();
    const updated = { ...current, ...settings };
    await synczbase.query(
        "INSERT INTO settings (id, data) VALUES ($1, $2) ON CONFLICT (id) DO UPDATE SET data = EXCLUDED.data",
        ['platform', JSON.stringify(updated)]
    );
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('syncz_platform_updated', { detail: updated }));
    }
};

export const getNotificationSettings = async (tenantId?: string): Promise<NotificationSettings> => {
    const defaults: NotificationSettings = { enableEmails: true, ownerEmail: '', contactEmail: '' };
    try {
        if (tenantId && tenantId !== 'root_platform') {
            const res = await synczbase.query("SELECT * FROM tenants WHERE id = $1 LIMIT 1", [tenantId]);
            const config = res?.[0] || {};
            const parsed = typeof config.notifications === 'string' ? JSON.parse(config.notifications) : config.notifications;
            if (parsed) return { ...defaults, ...parsed };
        }
        const res = await synczbase.query("SELECT * FROM settings WHERE id = $1 LIMIT 1", ['notifications']);
        if (res && res.length > 0) {
            const parsed = parseSettingData<NotificationSettings>(res[0]);
            if (parsed) return { ...defaults, ...parsed };
        }
    } catch(e) {}
    return defaults;
};

export const updateNotificationSettings = async (settings: NotificationSettings, tenantId?: string) => {
    await synczbase.query(
        "INSERT INTO settings (id, data) VALUES ($1, $2) ON CONFLICT (id) DO UPDATE SET data = EXCLUDED.data",
        ['notifications', JSON.stringify(settings)]
    );
    if (tenantId && tenantId !== 'root_platform') {
        try {
            await synczbase.query(
                `UPDATE tenants SET notifications = $1 WHERE id = $2`,
                [JSON.stringify(settings), tenantId]
            );
        } catch (e) {}
    }
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('syncz_notifications_updated', { detail: settings }));
    }
};

export const getFinancialSettings = async (tenantId?: string): Promise<FinancialSettings> => {
    const defaults = { defaultStripeProductId: '', stripeEnabled: true, szoinPriceId: '', allowTenantStripe: true };
    try {
        if (tenantId && tenantId !== 'root_platform') {
            const res = await synczbase.query("SELECT * FROM tenants WHERE id = $1 LIMIT 1", [tenantId]);
            const data = res?.[0]?.financial;
            const parsed = typeof data === 'string' ? JSON.parse(data) : data;
            if (parsed) return { ...defaults, ...parsed };
        }
        const res = await synczbase.query("SELECT * FROM settings WHERE id = $1 LIMIT 1", ['financial']);
        if (res && res.length > 0) {
            const parsed = parseSettingData<FinancialSettings>(res[0]);
            if (parsed) return { ...defaults, ...parsed };
        }
    } catch(e) {}
    return defaults;
};

export const updateFinancialSettings = async (settings: FinancialSettings, tenantId?: string) => {
    await synczbase.query(
        "INSERT INTO settings (id, data) VALUES ($1, $2) ON CONFLICT (id) DO UPDATE SET data = EXCLUDED.data",
        ['financial', JSON.stringify(settings)]
    );
    if (tenantId && tenantId !== 'root_platform') {
        try {
            await synczbase.query(
                `UPDATE tenants SET financial = $1 WHERE id = $2`,
                [JSON.stringify(settings), tenantId]
            );
        } catch (e) {}
    }
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('syncz_financial_updated', { detail: settings }));
    }
};

export const getInvoiceSettings = async (tenantId?: string): Promise<InvoiceSettings> => {
    const defaults = { businessName: 'My Business', address: '', email: '', legalRegion: 'PK' as const };
    try {
        if (tenantId && tenantId !== 'root_platform') {
            const res = await synczbase.query("SELECT * FROM tenants WHERE id = $1 LIMIT 1", [tenantId]);
            const data = res?.[0]?.invoice;
            const parsed = typeof data === 'string' ? JSON.parse(data) : data;
            if (parsed) return { ...defaults, ...parsed };
        }
        const res = await synczbase.query("SELECT * FROM settings WHERE id = $1 LIMIT 1", ['invoice']);
        if (res && res.length > 0) {
            const parsed = parseSettingData<InvoiceSettings>(res[0]);
            if (parsed) return { ...defaults, ...parsed };
        }
    } catch(e) {}
    return defaults;
};

export const updateInvoiceSettings = async (settings: InvoiceSettings, tenantId?: string) => {
    await synczbase.query(
        "INSERT INTO settings (id, data) VALUES ($1, $2) ON CONFLICT (id) DO UPDATE SET data = EXCLUDED.data",
        ['invoice', JSON.stringify(settings)]
    );
    if (tenantId && tenantId !== 'root_platform') {
        try {
            await synczbase.query(
                `UPDATE tenants SET invoice = $1 WHERE id = $2`,
                [JSON.stringify(settings), tenantId]
            );
        } catch (e) {}
    }
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('syncz_invoice_updated', { detail: settings }));
    }
};

export const uploadInvoiceLogo = async (file: File): Promise<string> => {
    return uploadDeliveryFile(file, `settings/invoice_logo_${Date.now()}`);
};

export const getBrandingSettings = async (tenantId?: string): Promise<BrandingSettings> => {
    const defaults: BrandingSettings = { 
        appTitle: 'SyncZ Portfolio',
        developerName: 'SyncZ Ltd',
        developerUrl: 'https://syncz.ltd'
    };
    try {
        if (tenantId && tenantId !== 'root_platform') {
            const res = await synczbase.query("SELECT * FROM tenants WHERE id = $1 LIMIT 1", [tenantId]);
            const data = res?.[0]?.branding;
            const parsed = typeof data === 'string' ? JSON.parse(data) : data;
            if (parsed) return { ...defaults, ...parsed };
            
            const globalRes = await synczbase.query("SELECT * FROM settings WHERE id = $1 LIMIT 1", ['branding']);
            if (globalRes && globalRes.length > 0) {
                const parsedGlobal = parseSettingData<BrandingSettings>(globalRes[0]);
                if (parsedGlobal) return { ...defaults, ...parsedGlobal };
            }
            return defaults;
        }

        const snap = await synczbase.query("SELECT * FROM settings WHERE id = $1 LIMIT 1", ['branding']);
        if (snap && snap.length > 0) {
            const parsed = parseSettingData<BrandingSettings>(snap[0]);
            if (parsed) return { ...defaults, ...parsed };
        }
    } catch(e) {
        console.warn("Offline or error loading branding", e);
    }
    return defaults;
};

export const updateBrandingSettings = async (settings: BrandingSettings, tenantId?: string) => {
    await synczbase.query(
        "INSERT INTO settings (id, data) VALUES ($1, $2) ON CONFLICT (id) DO UPDATE SET data = EXCLUDED.data",
        ['branding', JSON.stringify(settings)]
    );
    if (tenantId && tenantId !== 'root_platform') {
        try {
            await synczbase.query(
                `UPDATE tenants SET branding = $1 WHERE id = $2`,
                [JSON.stringify(settings), tenantId]
            );
        } catch (e) {}
    }
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('syncz_branding_updated', { detail: settings }));
        window.dispatchEvent(new CustomEvent('syncz_platform_updated', { detail: settings }));
    }
};

// Volatile in-memory runtime cache for marketplace and terminology
let inMemoryMarketplace: MarketplaceSettings | null = null;
let inMemoryTerminology: TerminologySettings | null = null;

export const uploadBrandingAsset = async (file: File): Promise<string> => {
    return uploadDeliveryFile(file, `settings/branding_${Date.now()}_${file.name}`);
};

export const getMarketplaceSettings = async (): Promise<MarketplaceSettings> => {
    if (inMemoryMarketplace) return inMemoryMarketplace;
    try {
        const snap = await synczbase.query("SELECT * FROM settings WHERE id = $1 LIMIT 1", ['marketplace']);
        if (snap && snap.length > 0) {
            const parsed = parseSettingData<MarketplaceSettings>(snap[0]);
            if (parsed) {
                inMemoryMarketplace = parsed;
                return parsed;
            }
        }
    } catch(e) {}

    // Default to DISABLED (Standalone Portfolio Mode)
    const defaults: MarketplaceSettings = { enabled: false, heroTitle: 'Creative Marketplace', heroSubtitle: 'Find professional portfolios and artists.' };
    inMemoryMarketplace = defaults;
    return defaults;
};

export const updateMarketplaceSettings = async (settings: MarketplaceSettings) => {
    inMemoryMarketplace = settings;
    await synczbase.query(
         "INSERT INTO settings (id, data) VALUES ($1, $2) ON CONFLICT (id) DO UPDATE SET data = EXCLUDED.data",
         ['marketplace', JSON.stringify(settings)]
    );
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('syncz_marketplace_updated', { detail: settings }));
        window.dispatchEvent(new CustomEvent('syncz_platform_updated', { detail: settings }));
    }
};

export const getIntegrationSecrets = async (tenantId?: string): Promise<IntegrationSecrets> => {
    try {
        const snap = await synczbase.query("SELECT * FROM settings WHERE id = $1 LIMIT 1", ['secrets']);
        if (snap && snap.length > 0) {
            const parsed = parseSettingData<IntegrationSecrets>(snap[0]);
            if (parsed) return parsed;
        }
    } catch(e) {}
    return {};
};

export const updateIntegrationSecrets = async (secrets: IntegrationSecrets) => {
    const snap = await synczbase.query("SELECT * FROM settings WHERE id = $1 LIMIT 1", ['secrets']);
    let currentSecrets: any = {};
    if (snap && snap.length > 0) {
        const parsed = parseSettingData<any>(snap[0]);
        if (parsed) currentSecrets = parsed;
    }
    const updatedSecrets = { ...currentSecrets, ...secrets };
    await synczbase.query(
         "INSERT INTO settings (id, data) VALUES ($1, $2) ON CONFLICT (id) DO UPDATE SET data = EXCLUDED.data",
         ['secrets', JSON.stringify(updatedSecrets)]
    );
};

export const getModuleSettings = async (tenantId?: string): Promise<ModuleSettings> => {
    const defaults: ModuleSettings = { 
        enableBookings: true, enableProjects: true, enablePortfolio: true, 
        enableTeam: true, enableFiles: true, enableContact: true,
        enableWorkshops: true, enableMarketing: true, enableCRM: true,
        enableFinances: true, enableLegal: true, enableForms: true,
        enableAiAssistant: true, enableSms: true, enableAnalytics: true, enableClientPortal: true,
        enableSyncZAuth: true, enableSyncZStorage: true, enableSyncZDatabase: true,
        enableSyncZWorkers: true, enableSyncZRealtime: true,
        disablePublicPages: false
    };

    let globalParsed: Partial<ModuleSettings> = {};
    let tenantParsed: Partial<ModuleSettings> = {};

    try {
        const snap = await synczbase.query("SELECT * FROM settings WHERE id = 'modules'");
        if (snap && snap.length > 0) {
            const parsed = parseSettingData<ModuleSettings>(snap[0]);
            if (parsed) globalParsed = parsed;
        }

        if (tenantId && tenantId !== 'root_platform') {
            const res = await synczbase.query("SELECT * FROM tenants WHERE id = $1 LIMIT 1", [tenantId]);
            const data = res?.[0]?.modules;
            const parsed = typeof data === 'string' ? JSON.parse(data) : data;
            if (parsed) tenantParsed = parsed;
        }
    } catch(e) {}
    
    return { ...defaults, ...globalParsed, ...tenantParsed };
};

export const updateModuleSettings = async (settings: ModuleSettings, tenantId?: string) => {
    // 1. Always persist globally to settings table for universal availability
    await synczbase.query(
        "INSERT INTO settings (id, data) VALUES ('modules', $1) ON CONFLICT (id) DO UPDATE SET data = EXCLUDED.data",
        [JSON.stringify(settings)]
    );

    // 2. Also persist to specific tenant if tenantId is provided
    if (tenantId && tenantId !== 'root_platform') {
        try {
            await synczbase.query(
                `UPDATE tenants SET modules = $1 WHERE id = $2`,
                [JSON.stringify(settings), tenantId]
            );
        } catch (e) {}
    }

    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('syncz_modules_updated', { detail: settings }));
        window.dispatchEvent(new CustomEvent('syncz_platform_updated', { detail: settings }));
    }
};

export const getLegalPageSettings = async (tenantId?: string): Promise<LegalPageSettings> => {
    try {
        if (tenantId && tenantId !== 'root_platform') {
            const res = await synczbase.query("SELECT * FROM tenants WHERE id = $1 LIMIT 1", [tenantId]);
            const config = res?.[0] || {};
            const parsed = typeof config.legalPages === 'string' ? JSON.parse(config.legalPages) : config.legalPages;
            if (parsed) return parsed;
        }
        const snap = await synczbase.query("SELECT * FROM settings WHERE id = 'legal_pages'");
        if (snap && snap.length > 0) {
            const parsed = parseSettingData<LegalPageSettings>(snap[0]);
            if (parsed) return parsed;
        }
    } catch(e) {}
    return {};
};

export const updateLegalPageSettings = async (settings: LegalPageSettings, tenantId?: string) => {
    await synczbase.query(
        "INSERT INTO settings (id, data) VALUES ('legal_pages', $1) ON CONFLICT (id) DO UPDATE SET data = EXCLUDED.data",
        [JSON.stringify(settings)]
    );
    if (tenantId && tenantId !== 'root_platform') {
        try {
            await synczbase.query(
                `UPDATE tenants SET "legalPages" = $1 WHERE id = $2`,
                [JSON.stringify(settings), tenantId]
            );
        } catch (e) {}
    }
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('syncz_legal_updated', { detail: settings }));
    }
};

// --- TERMINOLOGY ---

export const getGlobalTerminology = async (): Promise<TerminologySettings | null> => {
    if (inMemoryTerminology) return inMemoryTerminology;
    try {
        const snap = await synczbase.query("SELECT * FROM settings WHERE id = 'terminology'");
        if (snap && snap.length > 0) {
            const parsed = parseSettingData<TerminologySettings>(snap[0]);
            if (parsed) {
                inMemoryTerminology = parsed;
                return parsed;
            }
        }
        const genSnap = await synczbase.query("SELECT config, data FROM settings WHERE id = 'general'");
        if (genSnap && genSnap.length > 0) {
            const parsedGen = parseSettingData<any>(genSnap[0]);
            if (parsedGen?.terminology && typeof parsedGen.terminology === 'object') {
                inMemoryTerminology = parsedGen.terminology;
                return parsedGen.terminology;
            }
        }
    } catch(e) {}
    return null;
};

export const updateGlobalTerminology = async (terms: TerminologySettings) => {
    inMemoryTerminology = terms;
    try {
        await synczbase.query(
            "INSERT INTO settings (id, data) VALUES ('terminology', $1) ON CONFLICT (id) DO UPDATE SET data = EXCLUDED.data",
            [JSON.stringify(terms)]
        );
        // Also update inside general settings for full compatibility
        const genSnap = await synczbase.query("SELECT config, data FROM settings WHERE id = 'general'");
        if (genSnap && genSnap.length > 0) {
            const parsedGen = parseSettingData<any>(genSnap[0]) || {};
            parsedGen.terminology = terms;
            await synczbase.query(
                "INSERT INTO settings (id, data) VALUES ('general', $1) ON CONFLICT (id) DO UPDATE SET data = EXCLUDED.data",
                [JSON.stringify(parsedGen)]
            );
        }
    } catch (e) {
        console.warn("Failed to write terminology to database", e);
    }
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('syncz_terminology_updated', { detail: terms }));
        window.dispatchEvent(new Event('syncz_platform_updated'));
    }
};

export const updateTenantTerminology = async (tenantId: string, terms: TerminologySettings) => {
    inMemoryTerminology = terms;
    if (tenantId && tenantId !== 'root_platform') {
        try {
            const res = await synczbase.query("SELECT config FROM tenants WHERE id = $1 LIMIT 1", [tenantId]);
            let config: any = {};
            if (res && res.length > 0 && res[0].config) {
                config = typeof res[0].config === 'string' ? JSON.parse(res[0].config) : res[0].config;
            }
            config.terminology = terms;
            await synczbase.query(
                `UPDATE tenants SET terminology = $1, config = $2 WHERE id = $3`,
                [JSON.stringify(terms), JSON.stringify(config), tenantId]
            );
        } catch (e) {
            try {
                await synczbase.query(
                    `UPDATE tenants SET terminology = $1 WHERE id = $2`,
                    [JSON.stringify(terms), tenantId]
                );
            } catch (err) {}
        }
    } else {
        await updateGlobalTerminology(terms);
    }
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('syncz_terminology_updated', { detail: terms }));
        window.dispatchEvent(new Event('syncz_platform_updated'));
    }
};

// --- NOTIFICATION TEMPLATES (FULLY DYNAMIC) ---

const DEFAULT_TEMPLATES: NotificationTemplate[] = [
    {
        id: 'order_received',
        name: 'Order Received',
        message: "Hi {{client_name}},\\n\\nYour request for {{service_name}} has been received and is currently under review.\\n\\nWe will confirm your booking details shortly. Thank you for choosing us!\\n\\n— {{portfolio_name}}",
        enabled: true,
        variables: ['client_name', 'service_name', 'portfolio_name'],
        triggerDescription: 'Triggers when a client submits a new booking request.'
    },
    {
        id: 'booking_confirmed',
        name: 'Booking Confirmed',
        message: "Hi {{client_name}},\\n\\nGreat news! Your booking for {{service_name}} has been officially confirmed.\\n\\nWe look forward to collaborating with you. If you need to make any adjustments, feel free to manage it via the portal.\\n\\n— {{portfolio_name}}",
        enabled: true,
        variables: ['client_name', 'service_name', 'portfolio_name'],
        triggerDescription: 'Triggers when a booking request is approved.'
    },
    {
        id: 'invoice_created',
        name: 'Invoice Created',
        message: "Hi {{client_name}},\\n\\nAn invoice has been generated for your order of {{service_name}}.\\n\\nPlease click the secure link below to complete the payment:\\n{{payment_link}}\\n\\nThank you for your business!\\n\\n— {{portfolio_name}}",
        enabled: true,
        variables: ['client_name', 'service_name', 'payment_link', 'portfolio_name'],
        triggerDescription: 'Triggers when an invoice is issued.'
    }
];

export const getNotificationTemplates = async (): Promise<NotificationTemplate[]> => {
    try {
        const snap = await synczbase.query("SELECT * FROM notification_templates");
        if (!snap || snap.length === 0) {
            try { await seedNotificationTemplates(); } catch (seedErr) {}
            return DEFAULT_TEMPLATES;
        }
        return snap.map((row: any) => {
            if (typeof row.data === 'string') {
                try {
                    return JSON.parse(row.data);
                } catch (e) {
                    return row;
                }
            } else if (row.data && typeof row.data === 'object') {
                return row.data;
            }
            return row;
        }) as NotificationTemplate[];
    } catch (e) {
        return DEFAULT_TEMPLATES;
    }
};

export const updateNotificationTemplate = async (template: NotificationTemplate) => {
    await synczbase.query(
        "INSERT INTO notification_templates (id, data) VALUES ($1, $2) ON CONFLICT (id) DO UPDATE SET data = EXCLUDED.data",
        [template.id, JSON.stringify(template)]
    );
};

export const seedNotificationTemplates = async () => {
    for (const t of DEFAULT_TEMPLATES) {
        await updateNotificationTemplate(t);
    }
};
